if (!customElements.get('mae-gallery-video')) {
  customElements.define('mae-gallery-video', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.video = this.querySelector('video');
      this.button = this.querySelector('[data-video-play]');
      this.caption = this.querySelector('[data-video-caption]');
      this.pauseButton = this.querySelector('[data-video-pause]');
      this.gallery = this.closest('[data-product-gallery]');
      this.section = this.closest('[data-product-section]');
      this.slide = this.closest('[data-product-gallery-slide]');
      if (!this.video || !this.button || !this.gallery || !this.section) return;
      this.visible = false;
      this.active = false;
      this.request = 0;
      this.video.muted = true;
      this.video.controls = false;
      this.wantsPlay = false;
      this.setState('paused');
      this.update = () => {
        this.active = this.slide.dataset.mediaId === this.gallery.dataset.activeMediaId;
        if (!this.active || !this.visible || document.hidden) this.stop();
      };
      this.button.addEventListener('click', () => {
        if (this.wantsPlay) this.stop();
        else this.play();
      }, options);
      this.pauseButton?.addEventListener('click', () => this.stop(), options);
      this.video.addEventListener('pause', () => {
        if (!this.wantsPlay) this.setState('paused');
      }, options);
      this.video.addEventListener('ended', () => {
        this.stop();
      }, options);
      this.video.addEventListener('waiting', () => {
        if (this.wantsPlay) this.setState('loading');
      }, options);
      this.video.addEventListener('playing', () => {
        if (this.wantsPlay && this.active && this.visible && !document.hidden) this.setState('playing');
        else this.stop();
      }, options);
      this.video.addEventListener('error', () => {
        ++this.request;
        this.wantsPlay = false;
        this.loaded = false;
        this.setState('error');
      }, options);
      this.section.addEventListener('product:gallery-change', this.update, options);
      document.addEventListener('visibilitychange', this.update, options);
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting && entries[0].intersectionRatio >= 0.25;
        this.update();
      }, { threshold: [0, 0.25] });
      this.observer.observe(this.gallery);
      this.update();
    }
    load() {
      if (this.loaded) return;
      const sources = JSON.parse(this.dataset.sources || '[]');
      const mp4 = sources.filter(source => source.format === 'mp4').sort((a, b) => a.height - b.height);
      const target = navigator.connection?.saveData ? 480 : 1080;
      const chosen = mp4.find(source => source.height >= target) || mp4.at(-1) || sources.find(source => source.format === 'm3u8');
      if (!chosen) throw new Error('No playable video source');
      this.loaded = true;
      this.video.src = chosen.url;
      this.video.load();
    }
    async play() {
      const request = ++this.request;
      this.wantsPlay = true;
      this.setState('loading');
      try {
        if (!matchMedia('(prefers-reduced-motion: reduce)').matches) {
          await new Promise(resolve => setTimeout(resolve, 1050));
          if (request !== this.request) return;
        }
        this.load();
        if (this.video.ended) this.video.currentTime = 0;
        await this.video.play();
        if (request !== this.request) return;
        if (!this.active || !this.visible || document.hidden) {
          this.stop();
          return;
        }
        this.setState('playing');
      } catch (error) {
        if (request !== this.request) return;
        this.wantsPlay = false;
        this.setState('error');
      }
    }
    setState(state) {
      this.dataset.state = state;
      const playing = state === 'playing';

      this.button.disabled = false;
      this.dataset.moving = String(state === 'loading' || playing);
      if (this.pauseButton) this.pauseButton.disabled = !playing;

      if (this.caption) {
        this.caption.textContent = state === 'error' ? 'Try again' : 'See it in motion';
      }
      const labels = {
        paused: 'Play product video', playing: 'Pause product video',
        loading: 'Loading video. Click to cancel', error: 'Video could not load. Retry playback'
      };
      this.button.setAttribute('aria-label', labels[state]);
      this.button.setAttribute('aria-busy', String(state === 'loading'));
    }
    stop() {
      ++this.request;
      this.wantsPlay = false;
      this.video?.pause();
      if (this.button) this.setState('paused');
    }
    disconnectedCallback() {
      this.stop();
      this.observer?.disconnect();
      this.abort?.abort();
      this.abort = null;
    }
  });
}
